import { Dexie, type EntityTable } from 'dexie';

import type {
  FriendStreamUpdateDto,
  UserLocationInstanceV3Dto,
  UserLocationUserV3Dto,
} from '@furality/fox-client/models';

type Nullable<T> = {
  [P in keyof T]: T[P] | null;
};

export type FriendStreamLocation = UserLocationUserV3Dto &
  Nullable<UserLocationInstanceV3Dto> & {
    lastUpdated: Date | null;
  };

export const flattenStreamUpdate = (streamUpdate: FriendStreamUpdateDto): FriendStreamLocation => ({
  ...streamUpdate.user,
  location: streamUpdate.instance?.location ?? null,
  instanceName: streamUpdate.instance?.instanceName ?? null,
  furalityInstanceId: streamUpdate.instance?.furalityInstanceId ?? null,
  furalityEventId: streamUpdate.instance?.furalityEventId ?? null,
  lastUpdated: null,
});

export const db = new Dexie('FriendLocations') as Dexie & {
  friends: EntityTable<FriendStreamLocation, 'userId'>;
};

db.version(2)
  .stores({
    friends: 'userId, furalityInstanceId, furalityEventId, lastUpdated',
  })
  .upgrade((t) => {
    return t
      .table('friends')
      .toCollection()
      .modify((friend: FriendStreamLocation) => {
        friend.lastUpdated = null;
      });
  });

db.version(1).stores({
  friends: 'userId, furalityInstanceId, furalityEventId',
});

// 12 hours
const OLD_ENTRY_OFFSET_MS = 4.32e7;
export const wipeOldEntries = async () => {
  await db.transaction('readwrite', db.friends, async (t) => {
    // Indexeddb doesn't support indexing by null
    const nullEntries = (await t.friends.toArray()).filter((f) => !f.lastUpdated);
    const oldEntries = await t.friends
      .where('lastUpdated')
      .belowOrEqual(new Date(Date.now() - OLD_ENTRY_OFFSET_MS))
      .toArray();

    for (const ent of [...nullEntries, ...oldEntries]) {
      await t.friends.delete(ent.userId);
    }
  });
};

export const wipeAllEntries = async () => {
  await db.friends.clear();
};
